import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Avatar, Badge, Card, EmptyState, ErrorMessage, Field, Loading, Modal, SearchSelect, Tabs, Toggle, useAsync, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDateTime } from '../../lib/format';
import { PermissionExplanation, PermissionMatrix, scopeLabel, type Grant } from './PermissionMatrix';

export function UserDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can, user: me } = useAuth();
  const { notify } = useToast();

  const detail = useAsync(() => api.get(`/api/users/${id}`), [id]);
  const permissions = useAsync(() => api.get(`/api/users/${id}/permissions`), [id]);
  const catalog = useAsync(() => api.get('/api/roles/catalog/permissions'), []);
  const roles = useAsync(() => api.get('/api/roles').catch(() => []), []);
  const groups = useAsync(() => api.get('/api/groups').catch(() => []), []);
  const properties = useAsync(() => api.get('/api/properties').catch(() => []), []);
  const locations = useAsync(() => api.get('/api/routing/locations').catch(() => []), []);

  const [tab, setTab] = useState<'stamm' | 'rollen' | 'rechte' | 'effektiv'>('stamm');
  const [grants, setGrants] = useState<Grant[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [resetResult, setResetResult] = useState<string | null>(null);
  const [profile, setProfile] = useState<any>(null);

  useEffect(() => {
    if (permissions.data?.direct) {
      setGrants(
        permissions.data.direct.map((row: any) => ({
          permissionKey: row.permission_key,
          effect: row.effect,
          scope: row.scope,
        })),
      );
    }
  }, [permissions.data]);

  useEffect(() => {
    if (detail.data) {
      setProfile({
        firstName: detail.data.first_name,
        lastName: detail.data.last_name,
        email: detail.data.email,
        jobTitle: detail.data.job_title ?? '',
        employeeNo: detail.data.employee_no ?? '',
        phone: detail.data.phone ?? '',
        defaultLocationId: detail.data.default_location_id ?? null,
        isActive: !!detail.data.is_active,
        isGuest: !!detail.data.is_guest,
        validUntil: detail.data.valid_until?.slice(0, 10) ?? '',
        notes: detail.data.notes ?? '',
      });
    }
  }, [detail.data]);

  if (detail.loading || !profile) return <Loading />;
  if (detail.error) return <ErrorMessage error={detail.error} />;

  const data = detail.data;
  const mayEdit = can('admin.users.manage');
  const mayAssign = can('admin.users.permissions');

  /** Rechte, die bereits über Rollen/Gruppen wirken – als Hinweis in der Matrix. */
  const inherited: Record<string, { effect: string; scope: string; source: string }> = {};
  for (const [key, entry] of Object.entries<any>(permissions.data?.explanation ?? {})) {
    const fromRoleOrGroup = (entry.grants ?? []).filter((grant: any) => grant.sourceType !== 'user');
    if (fromRoleOrGroup.length) {
      const top = fromRoleOrGroup[fromRoleOrGroup.length - 1];
      inherited[key] = {
        effect: top.effect,
        scope: top.scope,
        source: top.viaGroupLabel ? `${top.viaGroupLabel} → ${top.sourceLabel}` : top.sourceLabel,
      };
    }
  }

  const save = async (payload: Record<string, unknown>, message: string) => {
    try {
      await api.patch(`/api/users/${id}`, payload);
      notify(message, 'success');
      detail.reload();
      permissions.reload();
    } catch (caught) {
      setError(caught);
    }
  };

  return (
    <div className="stack">
      <div className="module-header tone-danger">
        <Avatar firstName={data.first_name} lastName={data.last_name} size="lg" />
        <div className="module-text" data-pill="Benutzerakte">
          <h1>
            {data.first_name} {data.last_name}
          </h1>
          <p>
            {data.email} · {data.job_title ?? 'ohne Funktion'}
          </p>
          <div className="row" style={{ gap: 6, marginTop: 6 }}>
            {data.is_superadmin ? <Badge variant="danger">Superadmin</Badge> : null}
            <Badge variant={data.is_active ? 'success' : 'danger'}>{data.is_active ? 'Aktiv' : 'Deaktiviert'}</Badge>
            {data.is_guest ? <Badge variant="warning">Gastzugang</Badge> : null}
            {data.two_factor_enabled ? <Badge variant="info">2FA aktiv</Badge> : null}
          </div>
        </div>
        <div className="row">
          <button className="btn" onClick={() => navigate('/admin/users')}>
            Zurück
          </button>
          {can('admin.users.password') && (
            <button
              className="btn"
              onClick={async () => {
                const result = await api.post(`/api/users/${id}/reset-password`, {});
                setResetResult(result.password ?? null);
              }}
            >
              Passwort zurücksetzen
            </button>
          )}
          {can('admin.users.delete') && data.is_active && me?.id !== data.id && (
            <button
              className="btn danger"
              onClick={async () => {
                if (!window.confirm('Benutzerkonto wirklich deaktivieren?')) return;
                await api.delete(`/api/users/${id}`);
                notify('Benutzer deaktiviert.', 'success');
                detail.reload();
              }}
            >
              Deaktivieren
            </button>
          )}
        </div>
      </div>

      <ErrorMessage error={error} />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { key: 'stamm', label: 'Stammdaten' },
          { key: 'rollen', label: 'Rollen, Gruppen & Objekte' },
          { key: 'rechte', label: 'Einzelrechte', count: grants.length },
          { key: 'effektiv', label: 'Effektive Rechte' },
        ]}
      />

      {tab === 'stamm' && (
        <Card title="Stammdaten">
          <div className="stack">
            <div className="form-grid">
              <Field label="Vorname">
                <input value={profile.firstName} disabled={!mayEdit} onChange={(event) => setProfile({ ...profile, firstName: event.target.value })} />
              </Field>
              <Field label="Nachname">
                <input value={profile.lastName} disabled={!mayEdit} onChange={(event) => setProfile({ ...profile, lastName: event.target.value })} />
              </Field>
              <Field label="E-Mail">
                <input value={profile.email} disabled={!mayEdit} onChange={(event) => setProfile({ ...profile, email: event.target.value })} />
              </Field>
              <Field label="Funktion">
                <input value={profile.jobTitle} disabled={!mayEdit} onChange={(event) => setProfile({ ...profile, jobTitle: event.target.value })} />
              </Field>
              <Field label="Personalnummer">
                <input value={profile.employeeNo} disabled={!mayEdit} onChange={(event) => setProfile({ ...profile, employeeNo: event.target.value })} />
              </Field>
              <Field label="Telefon">
                <input value={profile.phone} disabled={!mayEdit} onChange={(event) => setProfile({ ...profile, phone: event.target.value })} />
              </Field>
              <Field
                label="Standard-Servicestandort"
                hint="Belegt Start und Ziel in der Tourenplanung vor."
              >
                <SearchSelect
                  value={String(profile.defaultLocationId ?? '')}
                  onChange={(wert) => setProfile({ ...profile, defaultLocationId: wert ? Number(wert) : null })}
                  options={(locations.data ?? []).map((location: any) => ({ value: String(location.id), label: `${location.name} ${location.city ? ` · ${location.city}` : ''}` }))}
                  placeholder="Kein Standort"
                  disabled={!mayEdit}
                />
              </Field>
              {profile.isGuest && (
                <Field label="Zugang gültig bis" hint="Nach Ablauf ist keine Anmeldung mehr möglich.">
                  <input type="date" value={profile.validUntil} disabled={!mayEdit} onChange={(event) => setProfile({ ...profile, validUntil: event.target.value })} />
                </Field>
              )}
            </div>

            <Field label="Notizen">
              <textarea value={profile.notes} disabled={!mayEdit} onChange={(event) => setProfile({ ...profile, notes: event.target.value })} />
            </Field>

            <div className="row" style={{ gap: 20 }}>
              <Toggle checked={profile.isActive} disabled={!mayEdit} onChange={(value) => setProfile({ ...profile, isActive: value })} label="Konto aktiv" />
              <Toggle checked={profile.isGuest} disabled={!mayEdit} onChange={(value) => setProfile({ ...profile, isGuest: value })} label="Externer Gastzugang" />
            </div>

            <div className="row between">
              <span className="small subtle">
                Angelegt am {formatDateTime(data.created_at)}
                {data.last_login_at ? ` · zuletzt angemeldet ${formatDateTime(data.last_login_at)}` : ''}
              </span>
              {mayEdit && (
                <button
                  className="btn primary"
                  onClick={() =>
                    save(
                      {
                        ...profile,
                        validUntil: profile.validUntil ? `${profile.validUntil} 23:59:59` : undefined,
                      },
                      'Stammdaten gespeichert.',
                    )
                  }
                >
                  Speichern
                </button>
              )}
            </div>
          </div>
        </Card>
      )}

      {tab === 'rollen' && (
        <div className="grid cols-2">
          <Card title="Rollen" subtitle="Bündeln fachliche Rechte">
            <div className="stack" style={{ gap: 8 }}>
              {(roles.data ?? []).map((role: any) => {
                const active = (data.roles ?? []).some((entry: any) => entry.id === role.id);
                return (
                  <label className="checkbox-row" key={role.id}>
                    <input
                      type="checkbox"
                      checked={active}
                      disabled={!mayAssign}
                      onChange={async (event) => {
                        const next = event.target.checked
                          ? [...(data.roles ?? []).map((entry: any) => entry.id), role.id]
                          : (data.roles ?? []).map((entry: any) => entry.id).filter((entry: number) => entry !== role.id);
                        await api.put(`/api/users/${id}/roles`, { roleIds: next });
                        detail.reload();
                        permissions.reload();
                      }}
                    />
                    <span style={{ flex: 1 }}>
                      <strong>{role.name}</strong>
                      <div className="small subtle">{role.description}</div>
                    </span>
                    <Badge>{role.permission_count} Rechte</Badge>
                  </label>
                );
              })}
            </div>
          </Card>

          <Card title="Gruppen" subtitle="Vererben Rollen, Rechte und Objektzuständigkeiten">
            <div className="stack" style={{ gap: 8 }}>
              {(groups.data ?? []).map((group: any) => {
                const active = (data.groups ?? []).some((entry: any) => entry.id === group.id);
                return (
                  <label className="checkbox-row" key={group.id}>
                    <input
                      type="checkbox"
                      checked={active}
                      disabled={!mayAssign}
                      onChange={async (event) => {
                        const next = event.target.checked
                          ? [...(data.groups ?? []).map((entry: any) => entry.id), group.id]
                          : (data.groups ?? []).map((entry: any) => entry.id).filter((entry: number) => entry !== group.id);
                        await api.put(`/api/users/${id}/groups`, { groupIds: next });
                        detail.reload();
                        permissions.reload();
                      }}
                    />
                    <span style={{ flex: 1 }}>
                      <strong>{group.name}</strong>
                      {group.parent_name && <div className="small subtle">↳ Untergruppe von {group.parent_name}</div>}
                    </span>
                  </label>
                );
              })}
              {(data.effectiveGroups ?? []).some((group: any) => group.inherited) && (
                <div className="alert info small">
                  Zusätzlich wirksam durch Vererbung:{' '}
                  {(data.effectiveGroups ?? [])
                    .filter((group: any) => group.inherited)
                    .map((group: any) => group.name)
                    .join(', ')}
                </div>
              )}
            </div>
          </Card>

          <Card title="Objektzuständigkeiten" subtitle="Bestimmt die Datensicht beim Scope „Meine Objekte“">
            <div className="stack" style={{ gap: 6 }}>
              {(properties.data ?? []).map((property: any) => {
                const active = (data.properties ?? []).some((entry: any) => entry.id === property.id);
                return (
                  <label className="checkbox-row" key={property.id}>
                    <input
                      type="checkbox"
                      checked={active}
                      disabled={!mayAssign}
                      onChange={async (event) => {
                        const next = event.target.checked
                          ? [...(data.properties ?? []).map((entry: any) => entry.id), property.id]
                          : (data.properties ?? []).map((entry: any) => entry.id).filter((entry: number) => entry !== property.id);
                        await api.put(`/api/users/${id}/properties`, { propertyIds: next });
                        detail.reload();
                      }}
                    />
                    <span>
                      {property.name} <span className="small subtle">{property.city}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </Card>

          <Card title="Qualifikationen" subtitle="Grundlage für automatische Zuweisungsvorschläge">
            {(data.skills ?? []).length === 0 ? (
              <EmptyState title="Keine Qualifikationen hinterlegt" />
            ) : (
              <div className="row" style={{ gap: 6 }}>
                {data.skills.map((skill: any) => (
                  <Badge key={skill.id} variant="info">
                    {skill.name} · Stufe {skill.level}
                  </Badge>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {tab === 'rechte' && (
        <Card
          title="Individuelle Einzelrechte"
          subtitle="Höchste Priorität: übersteuern Rollen- und Gruppenrechte – zum gezielten Erweitern oder Entziehen."
          actions={
            mayAssign ? (
              <button
                className="btn primary"
                onClick={async () => {
                  try {
                    await api.put(`/api/users/${id}/permissions`, { permissions: grants });
                    notify('Einzelrechte gespeichert.', 'success');
                    permissions.reload();
                    detail.reload();
                  } catch (caught) {
                    setError(caught);
                  }
                }}
              >
                Speichern
              </button>
            ) : null
          }
        >
          {catalog.loading ? (
            <Loading />
          ) : (
            <PermissionMatrix
              catalog={catalog.data?.permissions ?? []}
              modules={catalog.data?.modules ?? []}
              grants={grants}
              onChange={setGrants}
              readOnly={!mayAssign}
              inherited={inherited}
            />
          )}
        </Card>
      )}

      {tab === 'effektiv' && (
        <Card
          title="Effektive Rechte"
          subtitle="Ergebnis aus Rollen, Gruppen (inkl. Vererbung) und Einzelrechten – inklusive Herkunft"
        >
          {permissions.loading ? (
            <Loading />
          ) : permissions.data?.isSuperadmin ? (
            <div className="alert info">
              Dieser Benutzer ist <strong>Superadmin</strong> und besitzt alle Rechte mandantenübergreifend.
            </div>
          ) : (
            <PermissionExplanation
              catalog={catalog.data?.permissions ?? []}
              modules={catalog.data?.modules ?? []}
              effective={permissions.data?.effective ?? {}}
              explanation={permissions.data?.explanation ?? {}}
            />
          )}
        </Card>
      )}

      {resetResult !== null && (
        <Modal
          title="Passwort zurückgesetzt"
          onClose={() => setResetResult(null)}
          footer={
            <button className="btn primary" onClick={() => setResetResult(null)}>
              Verstanden
            </button>
          }
        >
          <p>Das neue Initialpasswort lautet:</p>
          <code className="mono" style={{ display: 'block', padding: 12, background: 'var(--surface-2)', borderRadius: 6, fontSize: 15 }}>
            {resetResult}
          </code>
          <p className="small subtle" style={{ marginTop: 10 }}>
            Der Benutzer wird bei der nächsten Anmeldung zur Änderung aufgefordert. Alle bestehenden Sitzungen
            wurden beendet.
          </p>
        </Modal>
      )}
    </div>
  );
}

export { scopeLabel };
